/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CluProjectCultureSwagger } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { httpService } from 'src/core/services/api/httpService';
import { CustomSkillProjectKind } from 'src/core/stores/app/types';
import { ResourceListResults } from 'src/core/types/ResourceListTypes';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { CustomTextProjectCultureSwagger } from 'src/customText/core/services/projects/customTextProjectSwaggers';
import { TrainingConfigVersionSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';

/**
 * Represents a service to perform global operations for a custom skill projects.
 */
export const getCustomSkillGlobalsService = createService(({ context, getEnvironment }) => {
    const { apiUrl: cluApiUrl, isProduction } = getEnvironment({ apiName: 'clu' });
    const { apiUrl: customTextApiUrl } = getEnvironment({ apiName: 'customText' });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const getUrlConfigs = (projectKind?: CustomSkillProjectKind) => {
        switch (projectKind) {
            case 'Conversation':
            case 'Orchestration':
                return { baseUrl: cluApiUrl, apiVersion: LuisConstants.Clu.AuthoringApiVersion };
            case 'CustomMultiLabelClassification':
            case 'CustomSingleLabelClassification':
            case 'CustomEntityRecognition':
            default:
                return { baseUrl: customTextApiUrl, apiVersion: LuisConstants.CustomText.AuthoringApiVersion };
        }
    };

    const getUrl = (resource: string, projectKind?: CustomSkillProjectKind) => {
        const { baseUrl, apiVersion } = getUrlConfigs(projectKind);
        const builder = urlBuilder()
            .baseUrl(baseUrl)
            .project()
            .resource('global')
            .resource(resource)
            .queryParam('showLatestVersionFlag', 'true')
            .queryParam('api-version', apiVersion);

        if (projectKind) {
            builder.queryParam('projectKind', projectKind);
        }

        return builder.build();
    };

    const getTrainingConfigVersions = async (
        projectKind: CustomSkillProjectKind,
        nextLink?: string
    ): Promise<ResourceListResults<TrainingConfigVersionSwagger>> => {
        const url = nextLink || getUrl('training-config-versions', projectKind);

        const { data: configVersions } = await httpService.get<ResourceListResults<TrainingConfigVersionSwagger>>(url, apiOptions);
        return configVersions;
    };

    const getProjectCultures = async (
        projectKind: CustomSkillProjectKind,
        nextLink?: string
    ): Promise<ResourceListResults<CluProjectCultureSwagger | CustomTextProjectCultureSwagger>> => {
        const url = nextLink || getUrl('languages', projectKind);

        const { data } = await httpService.get<ResourceListResults<CluProjectCultureSwagger | CustomTextProjectCultureSwagger>>(
            url,
            apiOptions
        );

        return data;
    };

    return {
        getProjectCultures,
        getTrainingConfigVersions
    };
});
